/**
 * The signed-in recording's upload state, held at module scope so that it
 * outlives every screen that shows it.
 *
 * Two halves: the published {@link ReflectionUploadSnapshot}, which
 * `useSyncExternalStore` subscribers render, and the retained {@link task}
 * state that the task's entry points (`reflectionUploadTask.ts`) and its run
 * (`performUpload.ts`) share.
 *
 * Internal to the task: only those two files import this module. Everything
 * else reads the snapshot through `reflectionUploadTask.ts`'s re-exports and
 * changes it through that file's entry points, because {@link task},
 * {@link patch} and {@link publishIdle} write shared state with no guard of
 * their own — the run-id and once-guard checks live in their two callers.
 *
 * Everything here is single-copy: one recording, one snapshot, one controller.
 * That is the product's shape — a person records one reflection at a time.
 */
import type { TrackFn } from "../analytics/track";
import type { LiveUpload } from "./liveUpload";
import type {
  ChunkTransport,
  NowFn,
  SleepFn,
  StartOptions,
  UploadApi,
  UploadStatus,
} from "./types";

/**
 * Everything the task needs from its caller to run one upload.
 *
 * Passed at `startReflectionUpload` and **retained for the life of the run**,
 * so a retry tapped from the dashboard, long after the recorder unmounted,
 * still has a transport and a tRPC caller to work with. They are values, not
 * hooks, so none of them keeps a component alive.
 */
export interface ReflectionUploadDeps {
  /**
   * The provider uid this recording belongs to, or `null` when no one is
   * signed in.
   *
   * Module state has no owner of its own: a keyed remount cannot reach it, so
   * the auth boundary's teardown passes straight by the retained recording.
   * Stamped here at `start()` and checked by `reconcileReflectionUploadOwner`,
   * whose notes say what goes wrong without it.
   */
  owner: string | null;
  /** The caller's authoritative byte cap, from `me.entitlements`. */
  maxUploadBytes: number;
  /** The two tRPC mutations, as plain promises. */
  uploadApi: UploadApi;
  /** Override for tests and the local-only e2e `?chunkBytes=` seam. */
  chunkSizeBytes?: number;
  /** Injected in tests; defaults to `xhrTransport`. */
  transport?: ChunkTransport;
  /** Injected clock for backoff, so tests never sleep. */
  sleep?: SleepFn;
  /** Injected wall clock for the run's `durationMs`; defaults to `Date.now`. */
  now?: NowFn;
  /** Injected analytics sink; defaults to the real one. */
  track?: TrackFn;
  /**
   * Called once, immediately after a `finalize` succeeds.
   *
   * A committed reflection has spent a monthly slot, so every cached
   * `usage.get` is now wrong. It is a callback rather than a query client
   * because the upload code must not import react-query: the run outlives
   * every component, and what it needs is the invalidation, not the library.
   */
  onFinalized?: () => void;
}

/** The task's published state — what `useResumableUpload` renders. */
export interface ReflectionUploadSnapshot {
  /** The current upload state. */
  status: UploadStatus;
  /** 0..1, monotonic — the display progress the bar renders. */
  progress: number;
  /**
   * The recording's byte length once pre-flight has measured it, or `null`
   * before that and after a reset.
   *
   * Published because the dashboard's pending card renders a real percentage
   * and the phases that carry `totalBytes` are not all of them: `creating` and
   * a terminal `failed` both have a tile to draw and neither states the size.
   */
  totalBytes: number | null;
  /**
   * The title the recording will be saved under — the chosen prompt — or `null`
   * for a take with no chooser behind it.
   *
   * The pending card is a reflection that does not exist yet, and this is the
   * only thing it can honestly name it by.
   */
  title: string | null;
  /**
   * When the run on screen began, epoch ms, or `null` while idle.
   *
   * The record page's handover offer and the hard-stall watchdog are both
   * elapsed-time judgements about the *upload*, not about a component's mount,
   * so the clock they read has to be the run's.
   */
  startedAt: number | null;
  /**
   * When the upload last got measurably closer to done, epoch ms, or `null`
   * while idle. Seeded to the run's start, then advanced on every rise in
   * display progress.
   *
   * This is what makes a **hard stall** detectable at all: a stalled upload and
   * a slow one look identical in `status`, which reports the same `uploading`
   * phase and the same committed offset for both. The difference is how long
   * the upload has stood still, and `performUpload.ts`'s `reportProgress`
   * explains why this mark and not the committed offset.
   */
  lastProgressAt: number | null;
}

/** The state a task with nothing to do publishes. */
const IDLE: ReflectionUploadSnapshot = Object.freeze({
  status: Object.freeze({ phase: "idle" }) as UploadStatus,
  progress: 0,
  totalBytes: null,
  title: null,
  startedAt: null,
  lastProgressAt: null,
});

let snapshot: ReflectionUploadSnapshot = IDLE;
const listeners = new Set<() => void>();

/** The retained state of the recording on the task. */
export interface ReflectionUploadTaskState {
  /** The recording, held for `retry()` until the run succeeds or is reset. */
  blob: Blob | null;
  /** The options the recording was started with, forwarded again on `retry()`. */
  startOptions: StartOptions;
  /** The deps of the run, retained so a retry can re-run without them. */
  deps: ReflectionUploadDeps | null;
  /** The provider uid the retained recording belongs to (see `ReflectionUploadDeps.owner`). */
  owner: string | null;
  /** The once-guard, scoped to one recording. */
  started: boolean;
  /**
   * The take being streamed while it records, until `start()` hands it to the
   * run. At most one: a new take's stream abandons the last one's.
   */
  live: LiveUpload | null;
  /** Cancels the run in flight. */
  controller: AbortController | null;
  /** Bumped once per run; a run whose id is no longer current publishes nothing. */
  runId: number;
  /** Display progress never goes backwards within one recording. */
  progressHighWater: number;
}

/**
 * The one recording's retained state, shared by the task's entry points and its
 * run. Mutable on purpose, and written only by `reflectionUploadTask.ts` and
 * `performUpload.ts` (see the module header).
 */
export const task: ReflectionUploadTaskState = {
  blob: null,
  startOptions: {},
  deps: null,
  owner: null,
  started: false,
  live: null,
  controller: null,
  runId: 0,
  progressHighWater: 0,
};

/** Wake every subscriber. */
function notify(): void {
  for (const listener of listeners) listener();
}

/** Publish a new snapshot and wake every subscriber. */
export function patch(next: Partial<ReflectionUploadSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  notify();
}

/** Publish the idle snapshot and wake every subscriber. */
export function publishIdle(): void {
  snapshot = IDLE;
  notify();
}

/**
 * Subscribe to task state, for `useSyncExternalStore`.
 *
 * @param listener - called on every published change.
 * @returns the unsubscribe function.
 */
export function subscribeReflectionUpload(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Read the current task state.
 *
 * The returned object is replaced, never mutated, so `useSyncExternalStore`'s
 * identity check is a correct change test.
 */
export function getReflectionUploadSnapshot(): ReflectionUploadSnapshot {
  return snapshot;
}

/**
 * The state the server renders.
 *
 * An upload is a thing a browser tab is doing; there is never one in flight
 * during SSR, and returning the shared frozen idle keeps React from seeing a
 * new object every server render.
 */
export function getReflectionUploadServerSnapshot(): ReflectionUploadSnapshot {
  return IDLE;
}
